import styled from '@emotion/styled';
import React from 'react';
import { css } from '@emotion/core';
import { ContentWrap, Css, ITheme, Responsive } from '../../../../../ui';
import { ISolicitationEntry } from '../../../../../contentful/types/ISolicitationEntry';
import { Color } from '../../../../../styles/Color';
import { formatFullDate } from '../../../../../i18n/formatFullDate';

interface ISolicitationDetailsHeaderInfo {
	solicitation: ISolicitationEntry;
}

export default function SolicitationDetailsHeaderInfo({
	solicitation,
}: ISolicitationDetailsHeaderInfo) {
	const {
		type,
		projectNumber,
		category,
		preSubmitConference,
		questionsDue,
		proposalDue,
	} = solicitation.fields;

	const solicitationDetailsMap = {
		Type: type,
		'Project Number': projectNumber,
		Category: category,
		'Pre-Bid Conference': preSubmitConference
			? formatFullDate(preSubmitConference, 'MM/dd/yyyy - hh:mm a', true)
			: null,
		'Questions Due': questionsDue
			? formatFullDate(questionsDue, 'MM/dd/yyyy - hh:mm a', true)
			: null,
		'Date Due': proposalDue
			? formatFullDate(proposalDue, 'MM/dd/yyyy - hh:mm a', true)
			: null,
	};

	const itemsToDisplay = Object.entries(solicitationDetailsMap).filter(
		([, value]) => value !== undefined,
	);

	return (
		<DetailsHeaderInfo>
			<StyledContentWrap amountOfItems={itemsToDisplay.length}>
				{itemsToDisplay.map(([label, value]) => (
					<InfoBlock key={label}>
						<small>{label}</small>
						<InfoValue>{value}</InfoValue>
					</InfoBlock>
				))}
			</StyledContentWrap>
		</DetailsHeaderInfo>
	);
}

const DetailsHeaderInfo = styled.div<{}, ITheme>`
	background-color: ${({ theme }) => theme.color.primaryLight};
`;

const StyledContentWrap = styled(ContentWrap)<{ amountOfItems: number }>`
	display: flex;
	justify-content: ${({ amountOfItems }) =>
		amountOfItems < 4 ? 'flex-start' : 'space-between'};
	${({ amountOfItems }) =>
		amountOfItems < 4 &&
		css`
			gap: ${Css.pxToRem(68)};
		`};

	padding: ${Css.pxToRem(28)} 0;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		flex-direction: column;
		padding: ${Css.pxToRem(8)} 0 ${Css.pxToRem(24)};
	}
`;

const InfoBlock = styled.div<{}, ITheme>`
	color: ${Color.OnBackground};

	small {
		color: ${Color.OnBackground};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		border-bottom: 1px solid ${({ theme }) => theme.color.brand};
		padding: ${Css.pxToRem(16)} 0 ${Css.pxToRem(4)};

		:last-of-type {
			border-bottom: none;
		}
	}
`;

const InfoValue = styled.div`
	font-weight: 500;
	font-size: ${Css.pxToRem(24)};

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		font-size: ${Css.pxToRem(16)};
	}
`;
